import React, { useMemo, useCallback } from 'react';
import TextField from 'apollo-react/components/TextField';
import Select from 'apollo-react/components/Select';
import MenuItem from 'apollo-react/components/MenuItem';
import Typography from '../../../../StyledTypography';
import Grid from 'apollo-react/components/Grid';
import FieldWrapper from '../../fields/FieldWrapper';
import HelperText from '../../fields/HelperText';
import { DEFAULT_CURRENCIES, DEFAULT_CURRENCY } from '../constants/currencies';
import styles from '../IDSField.module.css';

const PriceFieldInput = ({
  id,
  label,
  placeholder,
  value,
  onChange,
  onBlur,
  helperText,
  showError,
  disabled,
  required,
  columnStyle,
  options,
  defaultCurrency,
}) => {
  const priceValue = useMemo(() => {
    if (typeof value === 'object' && value !== null) {
      return {
        amount: value.amount ?? '',
        currency: value.currency || defaultCurrency || DEFAULT_CURRENCY,
      };
    }
    if (typeof value === 'string') {
      const trimmedValue = value.trim();
      const currencyMatch = trimmedValue.match(/^([A-Za-z]{3})\s+(.*)$/);
      if (currencyMatch) {
        return {
          currency: currencyMatch[1].toUpperCase(),
          amount: currencyMatch[2].trim(),
        };
      }
      return {
        amount: trimmedValue,
        currency: defaultCurrency || DEFAULT_CURRENCY,
      };
    }
    if (typeof value === 'number') {
      return {
        amount: String(value),
        currency: defaultCurrency || DEFAULT_CURRENCY,
      };
    }
    return {
      amount: '',
      currency: defaultCurrency || DEFAULT_CURRENCY,
    };
  }, [value, defaultCurrency]);
  const composePriceValue = useCallback((currency, amount) => {
    const normalizedCurrency = (currency || '').trim();
    const normalizedAmount =
      amount === null || amount === undefined ? '' : String(amount).trim();
    if (!normalizedCurrency && !normalizedAmount) return '';
    if (!normalizedAmount) return normalizedCurrency;
    if (!normalizedCurrency) return normalizedAmount;
    return `${normalizedCurrency} ${normalizedAmount}`;
  }, []);

  const priceCurrencies = useMemo(() => {
    if (options && options.length > 0) {
      return options;
    }
    return DEFAULT_CURRENCIES.map((c) => ({
      value: c.code,
      label: `${c.symbol} ${c.code}`,
    }));
  }, [options]);

  const handleAmountChange = useCallback(
    (e) => {
      const nextAmount = e.target.value;
      const nextCurrency =
        priceValue.currency || defaultCurrency || DEFAULT_CURRENCY;
      onChange(composePriceValue(nextCurrency, nextAmount));
    },
    [onChange, priceValue, defaultCurrency, composePriceValue]
  );

  const handleCurrencyChange = useCallback(
    (e) => {
      const nextCurrency = e.target.value;
      onChange(composePriceValue(nextCurrency, priceValue.amount));
    },
    [onChange, priceValue, composePriceValue]
  );

  return (
    <FieldWrapper style={columnStyle}>
      <Typography
        variant="bodySmall"
        className={`${styles.fieldLabel} ${styles.priceFieldLabel}`}
      >
        {label}
        {required && <span className={styles.requiredIndicatorRed}> *</span>}
      </Typography>
      <Grid container spacing={1} sx={{ width: '100%' }} alignItems="flex-end">
        <Grid size={8}>
          <TextField
            id={`${id}-amount`}
            name={`${id}-amount`}
            type="number"
            margin="none"
            placeholder={placeholder || '0.00'}
            value={priceValue.amount || ''}
            onChange={handleAmountChange}
            onBlur={onBlur}
            error={showError}
            disabled={disabled}
            fullWidth
            inputProps={{ min: 0, step: '0.01' }}
          />
        </Grid>
        <Grid size={4}>
          <Select
            id={`${id}-currency`}
            name={`${id}-currency`}
            margin="none"
            value={priceValue.currency || DEFAULT_CURRENCY}
            onChange={handleCurrencyChange}
            disabled={disabled}
            fullWidth
          >
            {priceCurrencies.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>
      <HelperText text={helperText} error={showError} />
    </FieldWrapper>
  );
};

export default PriceFieldInput;
